// runLifecycle.js — E1-WS1-T02 (refactor-deepening / F1, Turn/Run Lifecycle seam).
//
// Deep module owning the run's CONTROL PLANE — the cross-cutting state machine that
// today smears across three sites of SessionWorkspace.runAgent:
//   • setup (line ~849): setLocalRunning(true) + setLiveStatus(pensando) + create &
//     register the AbortController;
//   • onEvent preamble (line ~1043): watchdog.ping() on every loop event;
//   • finally (line ~1587): watchdog.cancel() + the SAME-CONTROLLER guard + the
//     control-plane reset (running off, liveStatus null, controller cleared, orphan
//     streaming bubbles closed).
//
// Deletion test (01-DOCTRINE): delete this module and the controller register/clear,
// the idle-watchdog arm/cancel, the same-controller race guard, and the reset scatter
// back across those three sites of the god-function — the complexity REAPPEARS spread,
// so it is DEEP (it concentrates a cross-cutting concern), not a pass-through.
//
// Timing is preserved byte-identical: begin() runs the early setters (immediate UI
// feedback before the setup awaits); armIdleWatchdog() is a SEPARATE call the caller
// makes only after pushMsg exists (same point the inline `armRunWatchdog` was armed),
// so the idle window still starts after setup — not earlier.
//
// PURE + injectable (all store/React effects arrive as deps); no React, no window/DOM.
// Wraps armRunWatchdog (kept as its own deep module — INV-2 idle watchdog) rather than
// re-implementing it. Interface: begin → { controller, armIdleWatchdog, onActivity,
// isCurrent, settle }.

import { armRunWatchdog, resolveRunIdleTimeout } from './runWatchdog.js';

/**
 * @param {{
 *   sessionId: string,
 *   controller: AbortController,
 *   setRunning: (on: boolean) => void,            // setLocalRunning (React state)
 *   setLiveStatus: (s: object | null) => void,
 *   setRunController: (id: string, ctrl: AbortController | null) => void, // agentRunStore
 *   getRunController: (id: string) => any,        // agentRunStore (same-controller guard)
 *   setRunMessages: (id: string, updater: (m: any[]) => any[]) => void,  // agentRunStore
 *   armWatchdog?: typeof armRunWatchdog,          // injectable for tests
 * }} deps
 * @returns {{
 *   controller: AbortController,
 *   armIdleWatchdog: (cfg: { idleMs?: number, dagTasks?: any[], onIdleNotice?: (idleMs:number) => void }) => void,
 *   onActivity: () => void,
 *   isCurrent: () => boolean,
 *   settle: () => boolean,
 * }}
 */
export function beginRunLifecycle({
  sessionId,
  controller,
  setRunning,
  setLiveStatus,
  setRunController,
  getRunController,
  setRunMessages,
  armWatchdog = armRunWatchdog,
}) {
  // ——— begin: immediate UI feedback + own the controller for this session ———
  setRunning(true);
  setLiveStatus({ phase: 'pensando' });
  setRunController(sessionId, controller);

  let watchdog = null;

  // Close any orphan "streaming" agent bubble so the feed never freezes mid-typing.
  const closeOrphanStreaming = () =>
    setRunMessages(sessionId, (m) => m.map((x) =>
      (x.role === 'agent' && x.streaming ? { ...x, streaming: false } : x)));

  const resetControlPlane = () => {
    closeOrphanStreaming();
    setRunning(false);
    setLiveStatus(null);
    setRunController(sessionId, null);
  };

  const isCurrent = () => getRunController(sessionId) === controller;

  return {
    controller,

    /**
     * Arm the IDLE watchdog. Called AFTER the run's feed writers exist (same point the
     * inline armRunWatchdog was armed), so the idle window starts after setup. onTimeout
     * does the SAME control-plane reset as the finally, then the caller's feed notice.
     */
    armIdleWatchdog({ idleMs, dagTasks, onIdleNotice } = {}) {
      const effectiveIdleMs = idleMs ?? resolveRunIdleTimeout(dagTasks);
      watchdog = armWatchdog({
        controller,
        sessionId,
        getRunController,
        idleMs: effectiveIdleMs,
        onTimeout: () => {
          resetControlPlane();
          onIdleNotice?.(effectiveIdleMs);
        },
      });
    },

    /** Loop activity resets the idle watchdog (no-op before arm / after settle). */
    onActivity() {
      watchdog?.ping();
    },

    isCurrent,

    /**
     * The run settled (done/error/abort): cancel the watchdog, then — ONLY if THIS run
     * is still the session's current controller (a newer run must not be clobbered) —
     * reset the control plane. Returns whether this run is current, so the caller runs
     * the remaining run-owned side effects (plan/outcome/TTS) inside the same guard.
     */
    settle() {
      watchdog?.cancel();
      if (!isCurrent()) return false;
      resetControlPlane();
      return true;
    },
  };
}
